import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CatalogCategory } from '@/types/catalog'

interface CategoryListProps {
  categories: CatalogCategory[]
  selectedCategoryId: string
  onSelect: (categoryId: string) => void
  className?: string
  testId?: string
}

export function CategoryList({
  categories,
  selectedCategoryId,
  onSelect,
  className,
  testId,
}: CategoryListProps) {
  return (
    <nav className={cn('flex flex-col gap-1 p-3', className)} data-testid={testId}>
      <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Categories
      </p>
      {categories.map((category) => {
        const active = category.id === selectedCategoryId
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => onSelect(category.id)}
            className={cn(
              'rounded-lg px-3 py-2 text-left text-sm transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-foreground hover:bg-muted',
            )}
          >
            {category.name}
          </button>
        )
      })}
    </nav>
  )
}

interface CategorySidebarProps {
  categories: CatalogCategory[]
  selectedCategoryId: string
  onSelect: (categoryId: string) => void
  className?: string
}

export function CategorySidebar(props: CategorySidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-border bg-card',
        props.className,
      )}
      data-testid="catalog-categories-sidebar"
    >
      <CategoryList
        categories={props.categories}
        selectedCategoryId={props.selectedCategoryId}
        onSelect={props.onSelect}
      />
    </aside>
  )
}

interface CategoryDrawerProps {
  open: boolean
  categories: CatalogCategory[]
  selectedCategoryId: string
  onSelect: (categoryId: string) => void
  onClose: () => void
}

export function CategoryDrawer({
  open,
  categories,
  selectedCategoryId,
  onSelect,
  onClose,
}: CategoryDrawerProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-40 lg:hidden" data-testid="catalog-categories-drawer">
      <button
        type="button"
        aria-label="Close categories drawer"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] border-r border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold">Categories</span>
          <button
            type="button"
            className="rounded-md p-1 hover:bg-muted"
            onClick={onClose}
            aria-label="Close"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
        <CategoryList
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onSelect={onSelect}
          testId="catalog-categories-drawer-panel"
        />
      </aside>
    </div>
  )
}
